import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Copy, FileUp, KeyRound, Pencil, Plus, Search, Trash2, UserCheck, UserX } from "lucide-react";
import Portrait from "@/components/Portrait";
import { papi, errText } from "../portalApi";
import { usePortal } from "../PortalApp";
import { Card, EmptyState, Field, Modal, PageHeader, Spinner, StatusBadge, hasPerm, inputCls } from "../ui";

const BLANK = {
  fullName: "", officialEmail: "", role: "employee", designation: "", department: "",
  brand: "Roy Career Solutions", employmentType: "Full-Time", status: "Active",
  joiningDate: "", reportingManager: "", workMode: "Remote", workLocation: "Bengaluru, Karnataka, India",
  bio: "", skills: "", showOnPublicWebsite: false,
};

export default function Employees() {
  const { emp: me } = usePortal();
  const canManage = hasPerm(me, "employees_manage");
  const canHrManage = hasPerm(me, "hr_manage");
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState(null);
  const [list, setList] = useState(null);
  const [filters, setFilters] = useState({ q: "", department: "", status: "", employmentType: "" });
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit', emp? }
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [docForm, setDocForm] = useState({ name: "", category: "General", file: null });
  const [uploading, setUploading] = useState(false);
  const [balances, setBalances] = useState([]);
  const [balEdits, setBalEdits] = useState({});
  const [balSaving, setBalSaving] = useState("");
  const [resetInfo, setResetInfo] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, m, l] = await Promise.all([
        papi.get("/hr/dashboard"),
        papi.get("/meta"),
        papi.get("/hr/employees", { params: filters }),
      ]);
      setStats(s.data);
      setMeta(m.data);
      setList(l.data);
    } catch (err) {
      toast.error(errText(err, "Could not load employees."));
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => {
    setForm({ ...BLANK, joiningDate: new Date().toISOString().slice(0, 10) });
    setDocForm({ name: "", category: "General", file: null });
    setBalances([]);
    setBalEdits({});
    setModal({ mode: "create" });
  };

  const resetPassword = async (emp) => {
    if (!window.confirm(`Reset ${emp.fullName}'s password? They will receive a temporary password and must set a new one on next sign in.`)) return;
    try {
      const r = await papi.post(`/hr/employees/${emp.id}/reset-password`);
      setResetInfo({ name: emp.fullName, tempPassword: r.data.tempPassword });
    } catch (err) {
      toast.error(errText(err, "Could not reset the password."));
    }
  };

  const deleteEmployee = async (emp) => {
    if (!window.confirm(`Permanently delete ${emp.fullName}? This removes their workspace account, Meet The Team profile, leave balances and all related records. This cannot be undone.`)) return;
    try {
      await papi.delete(`/hr/employees/${emp.id}`);
      toast.success(`${emp.fullName} deleted.`);
      load();
    } catch (err) {
      toast.error(errText(err, "Could not delete the employee."));
    }
  };

  const loadBalances = async (empId) => {
    try {
      const r = await papi.get(`/hr/employees/${empId}/leave-balances`);
      setBalances(r.data);
      const edits = {};
      r.data.forEach((b) => { edits[b.id] = b.allocated; });
      setBalEdits(edits);
    } catch {
      setBalances([]);
    }
  };

  const openEdit = (emp) => {
    setForm({
      fullName: emp.fullName, officialEmail: emp.officialEmail, role: emp.role,
      designation: emp.designation || "", department: emp.department || "", brand: emp.brand || "Roy Career Solutions",
      employmentType: emp.employmentType || "Full-Time", status: emp.status || "Active",
      joiningDate: emp.joiningDate || "", reportingManager: emp.reportingManager || "",
      workMode: emp.workMode || "Remote", workLocation: emp.workLocation || "",
      bio: emp.bio || "", skills: emp.skills || "", showOnPublicWebsite: Boolean(emp.showOnPublicWebsite),
    });
    setDocForm({ name: "", category: "General", file: null });
    setModal({ mode: "edit", emp });
    loadBalances(emp.id);
  };

  const saveBalance = async (b) => {
    setBalSaving(b.id);
    try {
      await papi.put(`/hr/leave-balances/${b.id}`, { allocated: parseInt(balEdits[b.id], 10) || 0 });
      toast.success(`${b.name} allocation updated.`);
      loadBalances(modal.emp.id);
    } catch (err) {
      toast.error(errText(err, "Could not update the balance."));
    } finally {
      setBalSaving("");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal.mode === "create") {
        await papi.post("/hr/employees", form);
        toast.success("Employee created. They can sign in with the temporary password RCS@2026 and will be asked to set their own.");
      } else {
        const { officialEmail, ...updates } = form;
        await papi.put(`/hr/employees/${modal.emp.id}`, updates);
        toast.success("Employee updated.");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(errText(err, "Could not save the employee."));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (emp) => {
    const isActive = ["Active", "Probation", "Internship", "Notice Period", "On Leave"].includes(emp.status);
    if (!window.confirm(`${isActive ? "Deactivate" : "Activate"} ${emp.fullName}?`)) return;
    try {
      await papi.post(`/hr/employees/${emp.id}/${isActive ? "deactivate" : "activate"}`);
      toast.success(`${emp.fullName} ${isActive ? "deactivated" : "activated"}.`);
      load();
    } catch (err) {
      toast.error(errText(err, "Action failed."));
    }
  };

  const uploadDoc = async (e) => {
    e.preventDefault();
    if (!docForm.file) return toast.error("Please choose a file.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", docForm.file);
      const up = await papi.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await papi.post(`/hr/employees/${modal.emp.id}/documents`, {
        name: docForm.name, category: docForm.category, fileUrl: up.data.url,
      });
      toast.success("Document added to the employee's vault.");
      setDocForm({ name: "", category: "General", file: null });
    } catch (err) {
      toast.error(errText(err, "Could not upload the document."));
    } finally {
      setUploading(false);
    }
  };

  if (!stats || !meta || !list) return <Spinner />;

  const roleOptions = Object.entries(meta.roles);

  return (
    <div data-testid="employees-page">
      <PageHeader title="Employees" sub="The employee master record — the single source of truth for the portal and the public Team page.">
        {canManage && (
          <button onClick={openCreate} className="btn-gold !px-5 !py-2.5 !text-xs" data-testid="add-employee-btn">
            <Plus className="h-4 w-4" aria-hidden /> Add Employee
          </button>
        )}
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7" data-testid="hr-stats">
        {[
          ["Total", stats.totalEmployees, "stat-total"],
          ["Active", stats.activeEmployees, "stat-active"],
          ["Interns", stats.interns, "stat-interns"],
          ["Present Today", stats.presentToday, "stat-present"],
          ["On Leave", stats.onLeaveToday, "stat-on-leave"],
          ["Pending Leaves", stats.pendingLeaveRequests, "stat-pending-leaves"],
          ["New (30d)", stats.newJoiners30d, "stat-new-joiners"],
        ].map(([label, val, tid]) => (
          <Card key={tid} testid={tid} className="!p-4 text-center">
            <p className="font-serif text-2xl text-navy-900">{val}</p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
          </Card>
        ))}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
          <input
            className={`${inputCls} !w-60 pl-10`}
            placeholder="Search name, email, ID"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            data-testid="employee-search"
            aria-label="Search employees"
          />
        </div>
        <select className={`${inputCls} !w-44`} value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })} data-testid="filter-department" aria-label="Filter by department">
          <option value="">All departments</option>
          {meta.departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className={`${inputCls} !w-40`} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status" aria-label="Filter by status">
          <option value="">All statuses</option>
          {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={`${inputCls} !w-40`} value={filters.employmentType} onChange={(e) => setFilters({ ...filters, employmentType: e.target.value })} data-testid="filter-type" aria-label="Filter by employment type">
          <option value="">All types</option>
          {meta.employmentTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {list.length === 0 ? (
        <EmptyState icon={Building2} title="No employees match" sub="Adjust your filters or add a new employee." testid="employees-empty" />
      ) : (
        <Card className="!p-0 overflow-hidden" testid="employees-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="employees-table">
              <thead>
                <tr className="border-b border-navy-900/10 bg-ivory text-left">
                  {["Employee", "ID", "Department", "Type", "Status", "Public Site", ""].map((h) => (
                    <th key={h} className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((emp) => (
                  <tr key={emp.id} className="border-b border-navy-900/5 last:border-0 hover:bg-ivory/60" data-testid={`employee-row-${emp.employeeId}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <Portrait name={emp.fullName} src={emp.photograph || ""} size="sm" testid={`employee-photo-${emp.employeeId}`} />
                        <div>
                          <p className="font-semibold text-navy-900">{emp.fullName}</p>
                          <p className="text-[11px] text-slate-400">{emp.designation || meta.roles[emp.role] || emp.role}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-500">{emp.employeeId}</td>
                    <td className="px-5 py-3 text-slate-600">{emp.department}</td>
                    <td className="px-5 py-3 text-slate-600">{emp.employmentType}</td>
                    <td className="px-5 py-3"><StatusBadge status={emp.status} /></td>
                    <td className="px-5 py-3 text-xs" data-testid={`employee-public-${emp.employeeId}`}>
                      {emp.showOnPublicWebsite ? <span className="font-semibold text-emerald-700">Visible</span> : <span className="text-slate-400">Hidden</span>}
                    </td>
                    <td className="px-5 py-3">
                      {canManage && (
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(emp)} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-gold/10 hover:text-gold-dark" aria-label={`Edit ${emp.fullName}`} data-testid={`edit-employee-${emp.employeeId}`}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={() => resetPassword(emp)} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-gold/10 hover:text-gold-dark" aria-label={`Reset password for ${emp.fullName}`} data-testid={`reset-pw-${emp.employeeId}`}>
                            <KeyRound className="h-4 w-4" />
                          </button>
                          <button onClick={() => deleteEmployee(emp)} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600" aria-label={`Delete ${emp.fullName}`} data-testid={`delete-employee-${emp.employeeId}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => toggleActive(emp)}
                            className={`rounded-lg p-2 transition-colors ${["Active", "Probation", "Internship", "Notice Period", "On Leave"].includes(emp.status) ? "text-slate-400 hover:bg-rose-50 hover:text-rose-600" : "text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"}`}
                            aria-label={`${["Active", "Probation", "Internship", "Notice Period", "On Leave"].includes(emp.status) ? "Deactivate" : "Activate"} ${emp.fullName}`}
                            data-testid={`toggle-employee-${emp.employeeId}`}
                          >
                            {["Active", "Probation", "Internship", "Notice Period", "On Leave"].includes(emp.status) ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={Boolean(modal)} onClose={() => setModal(null)} title={modal?.mode === "create" ? "Add Employee" : `Edit — ${modal?.emp?.fullName || ""}`} wide>
        {modal && (
          <form onSubmit={save} className="space-y-6" data-testid="employee-form">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Full Name">
                <input required className={inputCls} value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} data-testid="emp-fullname" />
              </Field>
              <Field label="Official Email">
                <input
                  type="email" required className={inputCls} value={form.officialEmail}
                  onChange={(e) => setForm({ ...form, officialEmail: e.target.value })}
                  disabled={modal.mode === "edit"} data-testid="emp-email"
                />
              </Field>
              <Field label="Portal Role">
                <select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="emp-role">
                  {roleOptions.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </Field>
              <Field label="Designation">
                <input className={inputCls} value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="emp-designation" />
              </Field>
              <Field label="Department">
                <input className={inputCls} value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="emp-department" />
              </Field>
              <Field label="Brand">
                <select className={inputCls} value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="emp-brand">
                  {meta.brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Employment Type">
                <select className={inputCls} value={form.employmentType} onChange={(e) => setForm({ ...form, employmentType: e.target.value })} data-testid="emp-type">
                  {meta.employmentTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="emp-status">
                  {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Joining Date">
                <input type="date" className={inputCls} value={form.joiningDate} onChange={(e) => setForm({ ...form, joiningDate: e.target.value })} data-testid="emp-joining" />
              </Field>
              <Field label="Reporting Manager">
                <input className={inputCls} list="manager-options" placeholder="Start typing a name" value={form.reportingManager} onChange={(e) => setForm({ ...form, reportingManager: e.target.value })} data-testid="emp-manager" />
                <datalist id="manager-options">
                  {list.map((x) => <option key={x.id} value={x.fullName} />)}
                </datalist>
              </Field>
              <Field label="Work Mode">
                <select className={inputCls} value={form.workMode} onChange={(e) => setForm({ ...form, workMode: e.target.value })} data-testid="emp-workmode">
                  <option>Remote</option>
                  <option>Office</option>
                  <option>Hybrid</option>
                </select>
              </Field>
              <Field label="Work Location">
                <input className={inputCls} value={form.workLocation} onChange={(e) => setForm({ ...form, workLocation: e.target.value })} data-testid="emp-location" />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Bio (shown on public Team page if visible)">
                <textarea rows={3} className={inputCls} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="emp-bio" />
              </Field>
              <Field label="Skills (comma separated)">
                <textarea rows={3} className={inputCls} value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} data-testid="emp-skills" />
              </Field>
            </div>

            <label className="flex items-center gap-3 rounded-xl border border-gold/30 bg-gold/5 px-4 py-3">
              <input type="checkbox" checked={form.showOnPublicWebsite} onChange={(e) => setForm({ ...form, showOnPublicWebsite: e.target.checked })} className="h-4 w-4 accent-gold" data-testid="emp-show-public" />
              <span className="text-sm text-slate-700">
                <span className="font-semibold text-navy-900">Show on public website.</span> When enabled, this person appears on the public Team page, synced from this master record.
              </span>
            </label>

            {modal.mode === "create" && (
              <p className="rounded-lg bg-sky-50 px-4 py-3 text-xs leading-relaxed text-sky-800" data-testid="emp-temp-password-note">
                The new employee signs in with their official email and the temporary password <span className="font-mono font-semibold">RCS@2026</span>, then sets their own password on first login.
              </p>
            )}

            <button type="submit" disabled={saving} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="emp-save">
              {saving ? "Saving…" : modal.mode === "create" ? "Create Employee" : "Save Changes"}
            </button>
          </form>
        )}

        {modal?.mode === "edit" && balances.length > 0 && (
          <div className="mt-8 border-t border-navy-900/10 pt-6">
            <p className="mb-4 font-serif text-base text-navy-900">Leave Balances ({balances[0]?.year})</p>
            <div className="space-y-2" data-testid="leave-balance-editor">
              {balances.map((b) => (
                <div key={b.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-navy-900/10 bg-ivory px-4 py-3" data-testid={`balance-row-${b.code}`}>
                  <div className="min-w-40 flex-1">
                    <p className="text-sm font-semibold text-navy-900">{b.name}</p>
                    <p className="text-[11px] text-slate-400">Used {b.used} · Available {b.available}</p>
                  </div>
                  {canHrManage ? (
                    <>
                      <input
                        type="number"
                        min="0"
                        className={`${inputCls} !w-24`}
                        value={balEdits[b.id] ?? b.allocated}
                        onChange={(e) => setBalEdits({ ...balEdits, [b.id]: e.target.value })}
                        aria-label={`${b.name} annual allocation`}
                        data-testid={`balance-input-${b.code}`}
                      />
                      <button
                        type="button"
                        onClick={() => saveBalance(b)}
                        disabled={balSaving === b.id || parseInt(balEdits[b.id], 10) === b.allocated}
                        className="btn-outline-dark !px-4 !py-2 !text-xs disabled:opacity-50"
                        data-testid={`balance-save-${b.code}`}
                      >
                        {balSaving === b.id ? "Saving…" : "Save"}
                      </button>
                    </>
                  ) : (
                    <p className="text-sm font-semibold text-navy-900">{b.allocated} / yr</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {modal?.mode === "edit" && canHrManage && (
          <div className="mt-8 border-t border-navy-900/10 pt-6">
            <p className="mb-4 font-serif text-base text-navy-900">Add Document to Vault</p>
            <form onSubmit={uploadDoc} className="grid gap-4 sm:grid-cols-4" data-testid="doc-upload-form">
              <Field label="Document Name">
                <input required className={inputCls} value={docForm.name} onChange={(e) => setDocForm({ ...docForm, name: e.target.value })} data-testid="doc-name" />
              </Field>
              <Field label="Category">
                <select className={inputCls} value={docForm.category} onChange={(e) => setDocForm({ ...docForm, category: e.target.value })} data-testid="doc-category">
                  {["General", "Offer Letter", "Internship Letter", "Policy", "Certificate", "Other"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="File (max 10 MB)">
                <input type="file" required className={`${inputCls} !py-2`} onChange={(e) => setDocForm({ ...docForm, file: e.target.files?.[0] || null })} data-testid="doc-file" />
              </Field>
              <div className="flex items-end">
                <button type="submit" disabled={uploading} className="btn-outline-dark w-full justify-center !px-4 !py-2.5 !text-xs disabled:opacity-60" data-testid="doc-upload-btn">
                  <FileUp className="h-4 w-4" aria-hidden /> {uploading ? "Uploading…" : "Upload"}
                </button>
              </div>
            </form>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(resetInfo)} onClose={() => setResetInfo(null)} title="Password Reset">
        {resetInfo && (
          <div className="space-y-4" data-testid="reset-pw-result">
            <p className="text-sm leading-relaxed text-slate-600">
              Temporary password for <span className="font-semibold text-navy-900">{resetInfo.name}</span>. Share it privately — it is shown only once, and they will be asked to set a new password on next sign in.
            </p>
            <div className="flex items-center gap-3 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3">
              <code className="flex-1 select-all font-mono text-sm font-semibold text-navy-900" data-testid="reset-pw-temp">{resetInfo.tempPassword}</code>
              <button onClick={() => { navigator.clipboard.writeText(resetInfo.tempPassword); toast.success("Copied to clipboard."); }} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-gold/10 hover:text-gold-dark" aria-label="Copy temporary password" data-testid="reset-pw-copy">
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
